import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { AllAgenciesRequested, selectAllAgencies } from 'src/app/core/admin';
import { AssignmentModel, AssignmentsService } from 'src/app/core/assignment';
import { AllUsersOfRoleRequested, selectValuersInStore } from 'src/app/core/auth';
import { AppState } from 'src/app/core/reducers';

@Component({
  selector: 'kt-duplicate-assignment-dialog',
  templateUrl: './duplicate-assignment-dialog.component.html',
  styleUrls: ['./duplicate-assignment-dialog.component.scss']
})
export class DuplicateAssignmentDialogComponent implements OnInit {
  private toeLoading$ = new BehaviorSubject<boolean>(true)
  private submitting$ = new BehaviorSubject<boolean>(false)
  loading$ = combineLatest([
    this.toeLoading$,
    this.submitting$
  ]).pipe(
    map(([toeLoading, submitting]) => {
      return toeLoading || submitting
    })
  )
  title$ = new BehaviorSubject<string>('')

  form = this.formBuilder.group({
    name: ['', [Validators.required]],
    project_manager_id: [0, [Validators.required]],
    agency_id: [0, [Validators.required]]
  })

  users$ = this.store.select(selectValuersInStore).pipe(
    map(users => 
      users.items.map(user => ({value: user.id, label: user.full_name}))
    )
  )
  agencies$ = this.store.select(selectAllAgencies).pipe(
    map(agencies =>
      agencies.map(agency => ({value: agency.id, label: agency.name}))
    )
  )
  selectedToes$ = new BehaviorSubject<Array<number>>([])
  toes$ = this.service.getToesListing(this.data.assignment.id).pipe(
    tap(_ => this.toeLoading$.next(false)),
    switchMap(response => {
      return this.selectedToes$.asObservable().pipe(
        map(selectedToes => {
          return response.data.map(toe => {
            return {
              id: toe.id,
              name: toe.name,
              checked: selectedToes.includes(toe.id)  
            }
          })
        })
      )
    })
  )

  constructor(
    public dialogRef: MatDialogRef<DuplicateAssignmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {assignment: AssignmentModel},
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private service: AssignmentsService,
  ) { 
    this.store.dispatch(new AllUsersOfRoleRequested({roleId: 2}))
    this.store.dispatch(new AllAgenciesRequested())

    this.title$.next(`Duplicate Assignment: ${data.assignment.name}`)
    this.form.patchValue({
      project_manager_id: data.assignment.valuer_id,
      agency_id: data.assignment.agency_id
    })
  }

  ngOnInit(): void {
  }

  onToeSelected(event: MatCheckboxChange, {id}: {id: number}) {
    const selectedToes = this.selectedToes$.value;
    if (event.checked) {
      selectedToes.push(id)
    } else {
      const indx = selectedToes.findIndex(toeId => toeId == id)
      selectedToes.splice(indx, 1)
    }
    this.selectedToes$.next(selectedToes)
  }

  onSubmit() {
    const controls = this.form.controls
    if (this.form.invalid) {
      Object.keys(controls).forEach(controlName => controls[controlName].markAsTouched())
      return;
    }

    this.submitting$.next(true);
    this.service.duplicateAssignment({
      name: controls.name.value ?? '',
      assignment_id: this.data.assignment.id,
      project_manager_id: controls.project_manager_id.value ?? 0,
      agency_id: controls.agency_id.value ?? 0,
      selectedToeIds: this.selectedToes$.value
    }).pipe(
      finalize(() => this.submitting$.next(false))
    ).subscribe({
      next: (res) => {
        this.dialogRef.close('success');
      },
      error: (err) => console.error('Error:', err)
    })
  }

  onClose() {
    this.dialogRef.close()
  }
}
